import { Building, Eye, MapPin, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FixedAsset } from '@/hooks/useFixedAssets';

interface FixedAssetMobileCardProps {
  asset: FixedAsset;
  onEdit: (asset: FixedAsset) => void;
  onDelete: (id: string) => void;
  onView: (asset: FixedAsset) => void;
}

export function FixedAssetMobileCard({ asset, onEdit, onDelete, onView }: FixedAssetMobileCardProps) {
  const typeLabel = asset.asset_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const formattedValue = Number(asset.value).toLocaleString(undefined, {
    style: 'currency',
    currency: asset.currency,
  });

  return (
    <Card className="w-full">
      <CardContent className="p-4 space-y-2">
        {/* Top row: name + Building icon + status badge */}
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-sm flex items-center gap-1.5 truncate">
            <Building className="h-4 w-4 text-muted-foreground shrink-0" />
            {asset.name}
          </span>
          <div className="flex gap-1 shrink-0">
            <Badge variant={asset.ownership_status === 'fully_owned' ? 'default' : 'secondary'} className={asset.ownership_status === 'fully_owned' ? 'bg-emerald-600 hover:bg-emerald-600' : 'bg-blue-100 text-blue-800 hover:bg-blue-100'}>
              {asset.ownership_status === 'fully_owned' ? 'Fully Owned' : 'Purchasing'}
            </Badge>
            <Badge variant={asset.status === 'owned' ? 'default' : 'outline'}>
              {asset.status.charAt(0).toUpperCase() + asset.status.slice(1)}
            </Badge>
          </div>
        </div>

        {/* Middle row: type badge + current value */}
        <div className="flex items-center justify-between gap-2">
          <Badge variant="secondary" className="text-xs">
            {typeLabel}
          </Badge>
          <span className="font-mono text-sm font-semibold">{formattedValue}</span>
        </div>

        {/* Bottom row: location (if present) + view/edit/delete buttons */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-muted-foreground flex items-center gap-1 truncate">
            {asset.location ? (
              <>
                <MapPin className="h-3 w-3 shrink-0" />
                {asset.location}
              </>
            ) : null}
          </span>
          <div className="flex gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="min-h-[44px] min-w-[44px]"
              onClick={() => onView(asset)}
              aria-label="View asset"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="min-h-[44px] min-w-[44px]"
              onClick={() => onEdit(asset)}
              aria-label="Edit asset"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="min-h-[44px] min-w-[44px]"
              onClick={() => onDelete(asset.id)}
              aria-label="Delete asset"
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
